import React, { useMemo } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import { lensTokens, surfaceHairline } from '~/configs/unifiedAnalyticsTheme';
import MUIProvider from '~/contexts/MUIProvider';
import { nativeLensId } from '~/nativeLenses/nativeLensId';

import { Alert, Box, Button, CircularProgress, MenuItem, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { FONT_STACK_SUISSE_INTL } from '~/components/brand/brandTokens';

import { UnifiedAnalyticsThemeProvider } from '~/pages/AskRichardUnified/UnifiedAnalyticsThemeProvider';
import PageChatButton from '~/pages/AskRichardUnified/page/PageChatButton';
import PageChatLayout from '~/pages/AskRichardUnified/page/PageChatLayout';
import { usePageBridge } from '~/pages/AskRichardUnified/page/pageBridge';
import type { PageField } from '~/pages/AskRichardUnified/page/pageContract';
import { validatePageFilters } from '~/pages/AskRichardUnified/page/pageFilters';

import { useGetReportingLeLensData } from '~/hooks/apis/reporting/useGetReportingLeLensData';

import { download } from '../RmMonitor/liveModel';

import ProductDashSelections from './ProductDashSelections';
import {
  OPTIONS_CARD_IDS,
  REPORT_CARD_IDS,
  type Selections,
  clearSelections,
  quickFilterSummary,
  readItemStatus,
  readSelections,
  selectionRequest,
  writeItemStatus,
  writeSelections,
} from './filterSelections';
import {
  CHAIN_COLUMNS,
  COMPARISONS,
  type Fact,
  PAGE_ID,
  VIEWS,
  type VendorFact,
  type ViewId,
  byGrain,
  chainCells,
  checkinGrid,
  headlines,
  hierarchyCsv,
  integer,
  matrixGrid,
  money,
  percent,
  performanceCsv,
  ranked,
  ratio,
  readFacts,
  sortByGross,
  vendorsOf,
  windowFor,
} from './liveDataAdapter';
import { resetProductScope } from './presentation';

function ScopeLine({
  row,
}: {
  row: {
    grossCp: number | null;
    netCp: number | null;
    marginCp: number | null;
    gmArCp: number | null;
    grossAllCp: number | null;
    nightsCp: number | null;
    packagesCp: number | null;
  };
}) {
  const tokens = lensTokens(useTheme());
  const metrics = [
    ['Gross sales', money(row.grossCp)],
    ['Net sales', money(row.netCp)],
    ['GM (AR)', money(row.gmArCp)],
    ['Gross margin', percent(ratio(row.marginCp, row.grossAllCp))],
    ['Room nights', integer(row.nightsCp)],
    ['Packages sold', integer(row.packagesCp)],
  ];
  return (
    <Box
      aria-label="Selected scope totals"
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        '@container (min-width: 500px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
        '@container (min-width: 700px)': { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' },
        '@container (min-width: 1450px)': { gridTemplateColumns: 'repeat(6, minmax(0, 1fr))' },
        gap: 1.5,
      }}
    >
      {metrics.map(([label, value]) => (
        <Box
          key={label}
          sx={{ minWidth: 0, bgcolor: tokens.surface, border: surfaceHairline(tokens), borderRadius: 2, p: 2 }}
        >
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              overflowWrap: 'anywhere',
              mt: 0.5,
            }}
          >
            {value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function sumFacts(rows: Fact[]) {
  const pick = (key: 'grossCp' | 'netCp' | 'marginCp' | 'gmArCp' | 'grossAllCp' | 'nightsCp' | 'packagesCp') =>
    rows.reduce((sum, row) => sum + (row[key] ?? 0), 0);
  return {
    grossCp: pick('grossCp'),
    netCp: pick('netCp'),
    marginCp: pick('marginCp'),
    gmArCp: pick('gmArCp'),
    grossAllCp: pick('grossAllCp'),
    nightsCp: pick('nightsCp'),
    packagesCp: pick('packagesCp'),
  };
}

function Spark({ values }: { values: number[] }) {
  const tokens = lensTokens(useTheme());
  const width = 220;
  const height = 56;
  const finite = values.filter((value) => Number.isFinite(value));
  if (finite.length < 2) return <Box sx={{ height }} />;
  const min = Math.min(...finite);
  const max = Math.max(...finite);
  const span = max - min || 1;
  const step = width / (values.length - 1);
  const points = values
    .map((value, index) => `${index * step},${height - ((value - min) / span) * (height - 4) - 2}`)
    .join(' ');
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden>
      <polyline fill="none" stroke={tokens.accent} strokeWidth="1.5" points={points} />
    </svg>
  );
}

function DataTable({
  headers,
  rows,
  onSelect,
  selected,
  label = 'Report results',
}: {
  headers: string[];
  rows: string[][];
  onSelect?: (name: string) => void;
  selected?: string;
  label?: string;
}) {
  const tokens = lensTokens(useTheme());
  return (
    <Box
      role="region"
      aria-label={`${label}, scroll to see all columns`}
      tabIndex={0}
      sx={{
        overflow: 'auto',
        maxHeight: 560,
        minWidth: 0,
        width: '100%',
        bgcolor: tokens.surface,
        border: surfaceHairline(tokens),
        borderRadius: 2,
        '&:focus-visible': { outline: `2px solid ${tokens.accent}`, outlineOffset: 2 },
      }}
    >
      <Box
        component="table"
        aria-label={label}
        sx={{
          width: '100%',
          minWidth: Math.max(640, headers.length * 112),
          borderCollapse: 'separate',
          borderSpacing: 0,
          fontSize: 13,
          fontVariantNumeric: 'tabular-nums',
          '& th, & td': {
            px: 1.75,
            py: 1.25,
            whiteSpace: 'nowrap',
            textAlign: 'right',
            borderBottom: `1px solid ${tokens.grid}`,
          },
          '& th:first-of-type, & td:first-of-type': { textAlign: 'left', minWidth: 160 },
          '& th': {
            position: 'sticky',
            top: 0,
            zIndex: 2,
            backgroundColor: tokens.surface,
            color: 'text.secondary',
            fontWeight: 600,
            fontSize: 12,
          },
          '& tbody tr:hover': { backgroundColor: tokens.plateHover },
          '& tbody tr:last-child td': { borderBottom: 0 },
        }}
      >
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header} scope="col">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row[0]}
              style={{ backgroundColor: selected && row[0] === selected ? alpha(tokens.accent, 0.1) : undefined }}
            >
              {row.map((cell, index) => (
                <td key={`${row[0]}-${index}`}>
                  {index === 0 && onSelect ? (
                    <Button
                      size="small"
                      aria-pressed={row[0] === selected}
                      onClick={() => onSelect(cell)}
                      sx={{
                        textTransform: 'none',
                        p: 0,
                        minWidth: 0,
                        textAlign: 'left',
                        justifyContent: 'flex-start',
                        color: tokens.accent,
                      }}
                    >
                      {cell}
                    </Button>
                  ) : (
                    cell
                  )}
                </td>
              ))}
            </tr>
          ))}
          {!rows.length ? (
            <tr>
              <td colSpan={headers.length}>No results for this selection.</td>
            </tr>
          ) : null}
        </tbody>
      </Box>
    </Box>
  );
}

export function ProductDashContent() {
  const tokens = lensTokens(useTheme());
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const requested = params.get('pd_view');
  const view: ViewId = VIEWS.includes(requested as ViewId) ? (requested as ViewId) : VIEWS[0];
  const fallback = windowFor(view);
  const from = params.get('pd_from') ?? fallback.from;
  const to = params.get('pd_to') ?? fallback.to;
  const comparison = COMPARISONS.some(([id]) => id === params.get('pd_comparison'))
    ? params.get('pd_comparison')!
    : 'month';
  const vertical = params.get('pd_vertical') === 'All' ? 'All' : 'Hotel';
  const chain = params.get('pd_chain') ?? '';
  const vendor = params.get('pd_vendor') ?? '';
  const activeSelection = view.startsWith('Chain') ? chain : view.startsWith('Vendor') ? vendor : '';
  // The subtitle reads as a sentence ("Compared with previous month"), so it keeps lower case.
  const comparisonLabel = (COMPARISONS.find(([id]) => id === comparison)?.[1] ?? 'Previous Month').toLowerCase();
  const chainScope = view.startsWith('Chain');
  const vendorScope = view.startsWith('Vendor');
  // Tableau's quick filters live in the URL, so a filtered view can be shared or reloaded.
  const selections = useMemo(() => readSelections(new URLSearchParams(location.search)), [location.search]);
  const itemStatus = useMemo(() => readItemStatus(new URLSearchParams(location.search)), [location.search]);
  const replaceSearch = (next: URLSearchParams) =>
    navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
  const update = (changes: Record<string, string>) => {
    const next = new URLSearchParams(location.search);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    replaceSearch(next);
  };
  const setSelections = (next: Selections) =>
    replaceSearch(writeSelections(next, new URLSearchParams(location.search)));
  const setItemStatus = (next: Array<string>) =>
    replaceSearch(writeItemStatus(next, new URLSearchParams(location.search)));
  const clearQuickFilters = () => replaceSearch(clearSelections(new URLSearchParams(location.search)));
  const period = { window: { from, to }, comparison: null };
  const query = useGetReportingLeLensData(
    nativeLensId(PAGE_ID),
    {
      period,
      vertical: [vertical],
      comparison,
      item_status: itemStatus,
      ...selectionRequest(selections, {
        chain: chainScope ? chain : undefined,
        vendor: vendorScope ? vendor : undefined,
      }),
    },
    false,
    undefined,
    undefined,
    undefined,
    REPORT_CARD_IDS,
  );
  // Dropdown values follow the window, vertical and item status, not the other quick filters,
  // so choosing a value does not reload every list.
  const optionsQuery = useGetReportingLeLensData(
    nativeLensId(PAGE_ID),
    { period, vertical: [vertical], item_status: itemStatus },
    true,
    undefined,
    undefined,
    undefined,
    OPTIONS_CARD_IDS,
  );
  const facts = useMemo(() => readFacts(query.data?.cards ?? []), [query.data?.cards]);
  const total = byGrain(facts, 'total')[0];
  const days = byGrain(facts, 'day');
  const chains = sortByGross(
    byGrain(facts, 'chain'),
    (row) => row.grossCp,
    (row) => row.label ?? '',
  );
  const destinations = sortByGross(
    byGrain(facts, 'destination'),
    (row) => row.grossCp,
    (row) => `${row.label ?? ''}|${row.label2 ?? ''}`,
  );
  const vendors = useMemo(() => vendorsOf(facts), [facts]);
  const ready = !query.isFetching && !query.error && Boolean(total);
  const schema: Record<string, PageField> = {
    view: { type: 'enum', values: VIEWS },
    from: { type: 'date' },
    to: { type: 'date' },
    comparison: { type: 'enum', values: COMPARISONS.map(([id]) => id) },
    vertical: { type: 'enum', values: ['Hotel', 'All'] },
    ...(chainScope ? { chain: { type: 'text' as const } } : {}),
    ...(vendorScope ? { vendor: { type: 'text' as const } } : {}),
  };
  usePageBridge({
    pageId: PAGE_ID,
    view: {
      title: `Product dash · ${view}`,
      ready,
      filterSchema: schema,
      filters: {
        view,
        from,
        to,
        comparison,
        vertical,
        ...(chainScope ? { chain: chain || null } : {}),
        ...(vendorScope ? { vendor: vendor || null } : {}),
      },
      // Quick-filter selections are read-only for chat: values such as offer names contain commas,
      // which the comma-separated edit format cannot carry safely.
      quickFilters: quickFilterSummary(selections, itemStatus),
      dataMode: 'live BigQuery through Lens',
      definitions: {
        population:
          'Item status from the Item Status quick filter, awaiting_dates and completed by default. Gross sales counts sale-side gross_sales_aud. GM% is margin_aud over gross_sales_aud. GM (AR) subtracts margin_aud_refund.',
        comparison: 'Previous window is the start and end shifted by the comparison period, matching Tableau DATEADD.',
      },
    },
    edit: (changes) => {
      if (!ready) throw new Error('Page data is still loading.');
      validatePageFilters(changes, schema);
      update(
        Object.fromEntries(
          Object.entries(changes).map(([key, value]) => [`pd_${key}`, value == null ? '' : String(value)]),
        ),
      );
    },
  });
  const tiles = headlines(
    total,
    [...days].sort((a, b) => (a.txnDate ?? '').localeCompare(b.txnDate ?? '')),
  );
  const compare = view.endsWith('Comparison');
  const chainHeaders = compare
    ? ['Chain Name', ...CHAIN_COLUMNS.slice(1).flatMap((column) => [column, '% Change'])]
    : [...CHAIN_COLUMNS];
  const vendorHeaders = compare
    ? ['Vendor', 'Chain', 'Gross Sales', '% Change', 'Net Sales', 'Package Sold']
    : ['Vendor', 'Chain', 'Gross Sales', 'Net Sales', 'Package Sold', 'Room Nights'];
  const exportCsv = () => {
    if (view === 'Performance Daily Summary') return download('product-dash-performance.csv', performanceCsv(days));
    if (view.startsWith('Chain'))
      return download(
        'product-dash-chain.csv',
        hierarchyCsv(
          chains.map((row) => ({ ...row, name: row.label ?? '' })),
          'Chain Name',
        ),
      );
    if (view === 'Destination Result Summary')
      return download(
        'product-dash-destination.csv',
        hierarchyCsv(
          destinations.map((row) => ({ ...row, name: row.label ?? '', extra: row.label2 ?? '' })),
          'Destination',
        ),
      );
    return download(
      'product-dash-vendor.csv',
      hierarchyCsv(
        vendors.map((row) => ({ ...row, name: row.vendor })),
        'Vendor',
      ),
    );
  };
  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        display: 'grid',
        gap: 3,
        minWidth: 0,
        bgcolor: tokens.canvas,
        color: 'text.primary',
        fontFamily: FONT_STACK_SUISSE_INTL,
        containerType: 'inline-size',
      }}
    >
      <Stack component="header" gap={1}>
        <Typography variant="overline" sx={{ color: tokens.accent, letterSpacing: '0.1em', fontSize: 11 }}>
          Product performance
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          gap={2}
        >
          <Typography
            component="h1"
            sx={{ fontFamily: FONT_STACK_SUISSE_INTL, fontSize: 23, fontWeight: 600, lineHeight: 1.3 }}
          >
            Sales, margin & demand
          </Typography>
          <PageChatButton />
        </Stack>
        <Typography color="text.secondary" variant="body2">
          {from} – {to} · Compared with {comparisonLabel} · {vertical === 'All' ? 'All verticals' : 'Hotel'}
        </Typography>
        <Typography color="text.secondary" variant="caption">
          {itemStatus.join() === 'awaiting_dates,completed'
            ? 'Sale-side results for completed items and items awaiting dates.'
            : `Sale-side results for item status ${itemStatus.join(', ')}.`}{' '}
          All monetary values are AUD.
        </Typography>
      </Stack>
      <Tabs
        value={view}
        aria-label="Product report sheets"
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          minWidth: 0,
          borderBottom: surfaceHairline(tokens),
          '& .MuiTab-root': { textTransform: 'none', minHeight: 48, color: 'text.secondary' },
          '& .Mui-selected': { color: `${tokens.accent} !important` },
          '& .MuiTabs-indicator': { bgcolor: tokens.accent },
        }}
        onChange={(_, next: ViewId) => {
          const window = windowFor(next);
          update({ pd_view: next, pd_from: window.from, pd_to: window.to, pd_chain: '', pd_vendor: '' });
        }}
      >
        {VIEWS.map((label) => (
          <Tab key={label} value={label} label={label} />
        ))}
      </Tabs>
      <Box
        component="section"
        aria-label="Report filters"
        sx={{
          bgcolor: tokens.surface,
          border: surfaceHairline(tokens),
          borderRadius: 2,
          p: { xs: 2, md: 2.5 },
          minWidth: 0,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Report scope</Typography>
          <Button
            size="small"
            onClick={() =>
              navigate(
                { pathname: location.pathname, search: resetProductScope(location.search, view) },
                { replace: true },
              )
            }
          >
            Reset filters
          </Button>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          gap={1.5}
          flexWrap="wrap"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={{
            '& .MuiTextField-root': {
              minWidth: { xs: 0, sm: 160 },
              flex: { xs: 'auto', sm: '1 1 160px', lg: '0 1 180px' },
            },
          }}
        >
          <TextField
            size="small"
            type="date"
            label="Start"
            value={from}
            onChange={(event) => update({ pd_from: event.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            type="date"
            label="End"
            value={to}
            onChange={(event) => update({ pd_to: event.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            select
            label="Comparison period"
            value={comparison}
            onChange={(event) => update({ pd_comparison: event.target.value })}
            sx={{ minWidth: 180 }}
          >
            {COMPARISONS.map(([id, label]) => (
              <MenuItem key={id} value={id}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            select
            label="Vertical"
            value={vertical}
            onChange={(event) => update({ pd_vertical: event.target.value })}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="Hotel">Hotel</MenuItem>
            <MenuItem value="All">All</MenuItem>
          </TextField>
          <Button variant="contained" onClick={exportCsv} disabled={!ready} sx={{ minHeight: 40 }}>
            Export CSV
          </Button>
          {(chainScope && chain) || (vendorScope && vendor) ? (
            <Button onClick={() => update(chainScope ? { pd_chain: '' } : { pd_vendor: '' })}>Clear selection</Button>
          ) : null}
        </Stack>
        <ProductDashSelections
          selections={selections}
          itemStatus={itemStatus}
          options={optionsQuery.data?.filterOptions ?? {}}
          loading={optionsQuery.isLoading}
          failed={optionsQuery.isError}
          onChange={setSelections}
          onItemStatusChange={setItemStatus}
          onClear={clearQuickFilters}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, overflowWrap: 'anywhere' }}>
          {view} ·{' '}
          {activeSelection
            ? `Selected ${chainScope ? 'chain' : 'vendor'}: ${activeSelection}`
            : `All ${chainScope ? 'chains' : vendorScope ? 'vendors' : 'results'} in this period`}{' '}
          · Filters apply to every result on this sheet.
        </Typography>
      </Box>
      {query.isFetching ? (
        <Stack
          role="status"
          direction="row"
          gap={1.5}
          alignItems="center"
          sx={{ p: 3, bgcolor: tokens.surface, borderRadius: 2 }}
        >
          <CircularProgress size={24} />
          <Typography variant="body2">Loading results for the selected scope…</Typography>
        </Stack>
      ) : null}
      {query.error ? (
        <Alert severity="error" action={<Button onClick={() => void query.refetch()}>Try again</Button>}>
          {query.error instanceof Error ? query.error.message : 'The query failed.'}
        </Alert>
      ) : null}
      {!query.isFetching && !query.error && !total ? (
        <Alert severity="info">No results for this scope. Adjust the dates or reset the filters.</Alert>
      ) : null}
      {view === 'Performance Daily Summary' && ready ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(5, minmax(0, 1fr))' },
            gap: 2,
          }}
        >
          {tiles.map((tile, index) => (
            <Box
              key={tile.label}
              sx={{
                minWidth: 0,
                border: surfaceHairline(tokens),
                borderTop: index < 3 ? `3px solid ${tokens.accent}` : surfaceHairline(tokens),
                bgcolor: tokens.surface,
                borderRadius: 2,
                p: 2.5,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {tile.label}
              </Typography>
              <Typography
                sx={{
                  mt: 1,
                  mb: 0.75,
                  fontSize: 26,
                  fontWeight: 600,
                  lineHeight: 1.25,
                  fontVariantNumeric: 'tabular-nums',
                  overflowWrap: 'anywhere',
                  color: tokens.accent,
                }}
              >
                {tile.value}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tile.change} {tile.movement}
              </Typography>
              <Spark values={tile.series} />
            </Box>
          ))}
        </Box>
      ) : null}
      {view.startsWith('Chain') && ready && total ? (
        <Stack gap={2}>
          <ScopeLine row={total} />
          <DataTable
            headers={chainHeaders}
            selected={chain}
            onSelect={(name) => update({ pd_chain: name === chain ? '' : name })}
            rows={chains.slice(0, 100).map((row) => [row.label ?? '', ...chainCells(row, compare)])}
          />
          {view === 'Chain Result Summary' ? <RankedChains rows={ranked(facts, 12)} /> : null}
          {view === 'Chain Result Summary' ? <Matrix facts={facts} /> : null}
        </Stack>
      ) : null}
      {view.startsWith('Vendor') && ready && total ? (
        <Stack gap={2}>
          <ScopeLine row={total} />
          {view === 'Vendor Details' ? <Checkin facts={facts} /> : null}
          {view === 'Vendor Result Summary' ? <MonthBars facts={facts} /> : null}
          <DataTable
            headers={vendorHeaders}
            selected={vendor}
            onSelect={(name) => update({ pd_vendor: name === vendor ? '' : name })}
            rows={vendors.slice(0, 100).map((row) => vendorRow(row, compare))}
          />
          <Typography variant="caption" color="text.secondary">
            {integer(vendors.length)} vendors. The table shows the first 100 by gross sales. Export CSV includes every
            vendor.
          </Typography>
        </Stack>
      ) : null}
      {view === 'Destination Result Summary' && ready ? (
        <Stack gap={2}>
          <ScopeLine row={sumFacts(destinations)} />
          <DataTable
            headers={['Destination', 'Country', ...CHAIN_COLUMNS.slice(1)]}
            rows={destinations
              .slice(0, 200)
              .map((row) => [row.label ?? '', row.label2 ?? '', ...chainCells(row, false)])}
          />
        </Stack>
      ) : null}
    </Box>
  );
}

function vendorRow(row: VendorFact, compare: boolean) {
  if (compare) {
    const delta = row.grossPp ? (row.grossCp ?? 0) / row.grossPp - 1 : null;
    return [
      row.vendor,
      row.chain ?? '',
      money(row.grossCp),
      delta === null ? '' : `${(delta * 100).toFixed(2)}%`,
      money(row.netCp),
      integer(row.packagesCp),
    ];
  }
  return [
    row.vendor,
    row.chain ?? '',
    money(row.grossCp),
    money(row.netCp),
    integer(row.packagesCp),
    integer(row.nightsCp),
  ];
}

function ReportSection({ title, children, note }: { title: string; children: React.ReactNode; note?: string }) {
  const tokens = lensTokens(useTheme());
  return (
    <Box
      component="section"
      sx={{
        minWidth: 0,
        bgcolor: tokens.surface,
        border: surfaceHairline(tokens),
        borderRadius: 2,
        p: { xs: 2, md: 2.5 },
      }}
    >
      <Typography component="h2" variant="subtitle2" sx={{ mb: 2 }}>
        {title}
      </Typography>
      {children}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        {note ?? 'Source: live BigQuery through Lens · Selected report period and scope.'}
      </Typography>
    </Box>
  );
}

function RankedChains({ rows }: { rows: Fact[] }) {
  const tokens = lensTokens(useTheme());
  const max = Math.max(...rows.map((row) => row.grossCp ?? 0), 1);
  return (
    <ReportSection
      title="Chains by gross sales"
      note="Top 12 chains · Gross sales AUD · Selected report period and scope."
    >
      <Stack gap={1.75}>
        {rows.map((row) => (
          <Box
            key={row.label}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr) auto', md: 'minmax(160px, 240px) minmax(0, 1fr) 128px' },
              alignItems: 'center',
              gap: 1.25,
            }}
          >
            <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
              {row.label}
            </Typography>
            <Box
              sx={{
                gridRow: { xs: 2, md: 'auto' },
                gridColumn: { xs: '1 / -1', md: 'auto' },
                height: 10,
                bgcolor: alpha(tokens.accent, 0.08),
                borderRadius: 1,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${((row.grossCp ?? 0) / max) * 100}%`,
                  minWidth: 2,
                  bgcolor: tokens.accent,
                  borderRadius: 1,
                }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'nowrap', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
            >
              {money(row.grossCp)}
            </Typography>
          </Box>
        ))}
      </Stack>
    </ReportSection>
  );
}

function MonthBars({ facts }: { facts: Fact[] }) {
  const tokens = lensTokens(useTheme());
  const months = byGrain(facts, 'month').sort((a, b) => (a.txnDate ?? '').localeCompare(b.txnDate ?? ''));
  const max = Math.max(...months.map((row) => row.grossCp ?? 0), 1);
  return (
    <ReportSection
      title="Gross sales by month"
      note="Gross sales AUD · Month of transaction · Selected report period and scope. Point to a bar for the month and value."
    >
      <Stack
        direction="row"
        alignItems="flex-end"
        gap={0.5}
        role="img"
        aria-label="Monthly gross sales in AUD"
        sx={{ height: 160, minWidth: 0 }}
      >
        {months.map((row) => (
          <Box
            key={row.txnDate}
            title={`${row.txnDate}: ${money(row.grossCp)}`}
            sx={{
              flex: 1,
              height: `${((row.grossCp ?? 0) / max) * 100}%`,
              bgcolor: tokens.accent,
              minHeight: 2,
              minWidth: 0,
            }}
          />
        ))}
      </Stack>
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {months[0]?.txnDate}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {months[months.length - 1]?.txnDate}
        </Typography>
      </Stack>
    </ReportSection>
  );
}

function Checkin({ facts }: { facts: Fact[] }) {
  const grid = checkinGrid(facts);
  return (
    <ReportSection
      title="Packages sold by check-in"
      note="Month × check-in weekday · Latest 18 months in the selected scope."
    >
      <DataTable
        label="Packages sold by check-in"
        headers={['Month', ...grid.weekdays]}
        rows={grid.months
          .slice(-18)
          .map((month) => [month, ...grid.weekdays.map((day) => integer(grid.value(month, day)))])}
      />
    </ReportSection>
  );
}

function Matrix({ facts }: { facts: Fact[] }) {
  const grid = matrixGrid(facts);
  return (
    <ReportSection title="Lead time by ADR range">
      <DataTable
        label="Lead time by ADR range"
        headers={['Lead time', ...grid.adrs]}
        rows={grid.leads.map((lead) => [lead, ...grid.adrs.map((adr) => integer(grid.value(lead, adr)))])}
      />
    </ReportSection>
  );
}

export default function ProductDashPage() {
  return (
    <MUIProvider>
      <UnifiedAnalyticsThemeProvider>
        <PageChatLayout>
          <ProductDashContent />
        </PageChatLayout>
      </UnifiedAnalyticsThemeProvider>
    </MUIProvider>
  );
}
